import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { read, utils } from 'xlsx';
import MarkdownRender from '../messages/MarkdownRenderer';
import { FileRenderer } from './renderers';
import ImageRenderer from './renderers/ImageRenderer';
import WebsiteRenderer from './renderers/WebsiteRenderer';

interface DocumentViewer {
  fileUrls: string[];
  isOpen: boolean;
  scratchpadContent?: string;
  activeTabIndex?: number;
}

type FileType =
  | 'csv'
  | 'xlsx'
  | 'text'
  | 'code'
  | 'bin'
  | 'image'
  | 'website'
  | null;

interface FileData {
  type: FileType;
  content: any;
  name: string;
  raw?: string | ArrayBufferLike;
  url?: string;
}

function DocumentViewerContainer() {
  const [files, setFiles] = useState<FileData[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [scratchpadContent, setScratchpadContent] = useState<string>('');
  const [scratchpadExpanded, setScratchpadExpanded] = useState<boolean>(false);

  const documentViewer = useAlchemystStoreForAi(
    (store) => store.documentViewer
  );
  const setStoreState = useAlchemystStoreForAi((store) => store.setStoreState);

  // Helper function to check if a string is a valid URL
  const isValidUrl = (urlString: string): boolean => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Automatically expand scratchpad when new content is added
  useEffect(() => {
    if (documentViewer.scratchpadContent) {
      // Always expand the scratchpad when scratchpad content is present regardless of previous state
      setScratchpadExpanded(true);
      
      // Also update the scratchpad content state
      setScratchpadContent(documentViewer.scratchpadContent);
    }
  }, [documentViewer.scratchpadContent]);

  useEffect(() => {
    if (documentViewer.fileUrls.length > 0) {
      // Create an array of promises for all file fetches
      const fetchPromises = documentViewer.fileUrls.map(
        async (urlOrContent) => {
          try {
            // First check if it's a valid website URL
            if (
              isValidUrl(urlOrContent) &&
              !(urlOrContent.split('/').pop() ?? '').includes('.')
            ) {
              return {
                type: 'website' as FileType,
                content: urlOrContent,
                name: new URL(urlOrContent).hostname,
                raw: urlOrContent,
                url: urlOrContent,
              };
            }

            // Check if it's a long piece of code (more than 10 lines)
            const lines = urlOrContent.split('\n');
            if (lines.length > 10) {
              return {
                type: 'code' as FileType,
                content: urlOrContent,
                name: 'Inline Code',
                raw: urlOrContent,
              };
            }

            // Then check if it's an image
            const isImage = await checkIfImage(urlOrContent);
            if (isImage) {
              return {
                type: 'image' as FileType,
                content: urlOrContent,
                name: getFileNameFromUrl(urlOrContent),
                raw: urlOrContent,
                url: urlOrContent,
              };
            }

            // If not a website, image, or code, try to fetch as text
            try {
              const response = await fetch(urlOrContent);
              const content = await response.text();

              return {
                type: 'text' as FileType,
                content: content,
                name: getFileNameFromUrl(urlOrContent),
                raw: content,
                url: urlOrContent,
              };
            } catch {
              // If fetch fails, treat as text content
              return {
                type: 'text' as FileType,
                content: urlOrContent,
                name: 'Text Content',
                raw: urlOrContent,
              };
            }
          } catch (error) {
            console.error('Error processing file content:', error);
            return null;
          }
        }
      );

      // Wait for all files to be fetched
      Promise.all(fetchPromises).then((newFiles) => {
        // Filter out any failed fetches
        const validFiles = newFiles.filter((file) => file !== null);

        setFiles((prevFiles) => {
          const updatedFiles = [...prevFiles];

          // Update or add each file
          validFiles.forEach((newFile) => {
            const existingIndex = updatedFiles.findIndex(
              (f) => f.url === newFile.url || f.content === newFile.content
            );
            if (existingIndex >= 0) {
              updatedFiles[existingIndex] = newFile;
            } else {
              updatedFiles.push(newFile);
            }
          });

          // If this is a new file being added, set the active tab to it
          if (
            validFiles.length > 0 &&
            documentViewer.activeTabIndex === updatedFiles.length - 1
          ) {
            setTimeout(() => setActiveTabIndex(updatedFiles.length - 1), 0);
          }

          return updatedFiles;
        });

        // Update active tab index from documentViewer state if provided
        if (typeof documentViewer.activeTabIndex === 'number') {
          setActiveTabIndex(documentViewer.activeTabIndex);
        }
      });
    }
  }, [
    documentViewer.fileUrls,
    documentViewer.scratchpadContent,
    documentViewer.activeTabIndex,
  ]);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileType = getFileType(file.name);

    try {
      const rawContent = await file.arrayBuffer();
      let newFile: FileData;

      if (fileType === 'csv' || fileType === 'xlsx') {
        const workbook = read(rawContent);
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const content = utils.sheet_to_json(firstSheet, { header: 1 });

        newFile = {
          type: fileType,
          content,
          name: file.name,
          raw: rawContent,
        };
      } else if (fileType === 'text' || fileType === 'code') {
        const textDecoder = new TextDecoder(
          file.type.includes('charset=')
            ? file.type.split('charset=')[1]
            : 'utf-8'
        );
        const textContent = textDecoder.decode(rawContent);

        newFile = {
          type: fileType,
          content: textContent,
          name: file.name,
          raw: textContent,
        };
      } else {
        newFile = {
          type: 'bin',
          content: null,
          name: file.name,
          raw: rawContent,
        };
      }

      setFiles((prevFiles) => [...prevFiles, newFile]);
      setActiveTabIndex(files.length);
    } catch (error) {
      console.error('Error reading file:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
    // Also update the documentViewer state to keep it in sync
    setStoreState({
      documentViewer: {
        ...documentViewer,
        activeTabIndex: newValue,
      },
    });
  };

  const handleCloseTab = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    setFiles((prevFiles) => {
      const newFiles = prevFiles.filter((_, i) => i !== index);
      if (activeTabIndex >= newFiles.length) {
        setActiveTabIndex(Math.max(0, newFiles.length - 1));
      }
      return newFiles;
    });

    // Also update the documentViewer state in the store
    setStoreState({
      documentViewer: {
        ...documentViewer,
        fileUrls: documentViewer.fileUrls.filter((_, i) => i !== index),
        activeTabIndex:
          activeTabIndex >= documentViewer.fileUrls.length - 1
            ? Math.max(0, documentViewer.fileUrls.length - 2)
            : activeTabIndex,
      },
    });
  };

  const getFileType = (fileName: string): FileType => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'csv':
        return 'csv';
      case 'xlsx':
      case 'xls':
        return 'xlsx';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'svg':
        return 'image';
      case 'txt':
      case 'md':
      case 'markdown':
        return 'text';
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
      case 'py':
      case 'java':
      case 'go':
      case 'rs':
      case 'json':
      case 'yaml':
      case 'yml':
      case 'sh':
      case 'bash':
      case 'sql':
      case 'css':
      case 'scss':
      case 'html':
      case 'xml':
        return 'code';
      default:
        return 'bin';
    }
  };

  // Helper function to check if a URL is an image
  const checkIfImage = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  // Helper function to get filename from URL
  const getFileNameFromUrl = (url: string): string => {
    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/');
    return pathSegments[pathSegments.length - 1] || 'document';
  };

  const renderFileContent = () => {
    if (files.length === 0) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ height: '100%', p: 3 }}
        >
          <Typography>No files to show yet.</Typography>
        </Stack>
      );
    }

    const currentFile = files[activeTabIndex] ?? 'website';

    if (currentFile.type === 'website') {
      return <WebsiteRenderer url={currentFile.content} />;
    } else if (currentFile.type === 'image') {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 2,
            overflow: 'auto',
          }}
        >
          {/* <img
            src={currentFile.content}
            alt={currentFile.name}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          /> */}
          <ImageRenderer url={currentFile.content} />
        </Box>
      );
    } else if (currentFile.type === 'code') {
      return (
        <FileRenderer
          name={currentFile.name}
          content={currentFile.raw!}
          type="text"
        />
      );
    } else if (currentFile.type === 'csv' || currentFile.type === 'xlsx') {
      return (
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {currentFile.content[0]?.map(
                  (header: string, index: number) => (
                    <TableCell
                      key={index}
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: (theme) =>
                          alpha(theme.palette.background.paper, 0.95),
                        color: (theme) => theme.palette.text.primary,
                      }}
                    >
                      {header}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentFile.content
                .slice(1)
                .map((row: any[], rowIndex: number) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.background.paper, 0.05),
                      },
                      '&:hover': {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.background.paper, 0.1),
                      },
                    }}
                  >
                    {row.map((cell, cellIndex) => (
                      <TableCell
                        key={cellIndex}
                        align={typeof cell === 'number' ? 'right' : 'left'}
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                        }}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      return (
        <FileRenderer
          name={currentFile.name}
          content={currentFile.raw!}
          type={currentFile.type === 'bin' ? 'bin' : 'text'}
          url={currentFile.url}
        />
      );
    }
  };

  const markdownStyles = {
    color: (theme: any) => (theme.palette.mode === 'dark' ? 'white' : 'black'),
    '& h1, & h2, & h3, & p, & ul, & ol, & li, & code, & pre, & blockquote, & table, & a':
      {
        color: (theme: any) =>
          theme.palette.mode === 'dark' ? 'white' : 'black',
      },
    '& h1': {
      fontSize: '1.5rem',
      marginTop: 0,
      marginBottom: 1,
    },
    '& h2': {
      fontSize: '1.25rem',
      marginTop: 1.5,
      marginBottom: 1,
    },
    '& h3': {
      fontSize: '1.1rem',
      marginTop: 1.25,
      marginBottom: 0.75,
    },
    '& p': {
      marginBottom: 1,
    },
    '& ul, & ol': {
      marginTop: 0.5,
      marginBottom: 1,
      paddingLeft: 2,
    },
    '& li': {
      marginBottom: 0.25,
    },
    '& code': {
      backgroundColor: (theme: any) =>
        theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.1)'
          : 'rgba(0,0,0,0.1)',
      padding: '0.2em 0.4em',
      borderRadius: '3px',
      fontSize: '0.875rem',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: (theme: any) =>
        theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.05)'
          : 'rgba(0,0,0,0.05)',
      padding: 2,
      borderRadius: '4px',
      overflow: 'auto',
      '& code': {
        padding: 0,
        backgroundColor: 'transparent',
      },
    },
    '& blockquote': {
      borderLeft: (theme: any) =>
        `4px solid ${theme.palette.mode === 'dark' ? 'white' : 'black'}`,
      margin: '1em 0',
      padding: '0.5em 1em',
      backgroundColor: (theme: any) =>
        theme.palette.mode === 'dark'
          ? 'rgba(255,255,255,0.03)'
          : 'rgba(0,0,0,0.03)',
    },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginBottom: 1,
      '& th': {
        backgroundColor: (theme: any) =>
          theme.palette.mode === 'dark' ? 'black' : '#EAEAEA',
        fontWeight: 'bold',
        padding: '0.5em',
        textAlign: 'left',
      },
      '& td': {
        padding: '0.5em',
        borderBottom: (theme: any) =>
          `1px solid ${
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.2)'
              : 'rgba(0,0,0,0.2)'
          }`,
      },
      '& tr:nth-of-type(odd)': {
        backgroundColor: (theme: any) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.05)'
            : 'rgba(0,0,0,0.05)',
      },
    },
    '& a': {
      textDecoration: 'underline',
      '&:hover': {
        color: (theme: any) =>
          theme.palette.mode === 'dark' ? '#EAEAEA' : '#666666',
      },
    },
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        gap: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          height: scratchpadExpanded ? '70%' : 'calc(100% - 56px)',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, 0.8),
          transition: 'height 0.3s ease',
          mb: scratchpadExpanded ? 2 : 0,
          borderRadius: '12px',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tabs
              value={activeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flex: 1 }}
            >
              {files.map((file, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {file.name.length > 10
                        ? `${file.name.substring(0, 10)}...`
                        : file.name}
                      <IconButton
                        size="small"
                        onClick={(e) => handleCloseTab(e, index)}
                        sx={{ ml: 1 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                />
              ))}
            </Tabs>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </Stack>
        </Box>
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>{renderFileContent()}</Box>
      </Paper>

      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: scratchpadExpanded ? '30%' : 'auto',
          backgroundColor: (theme) =>
            alpha(theme.palette.background.paper, 0.8),
          transition: 'height 0.3s ease',
          marginTop: scratchpadExpanded ? 0 : 'auto',
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2,
            paddingBottom: scratchpadExpanded ? 1 : 2,
            cursor: 'pointer',
            borderBottom: scratchpadExpanded
              ? '1px solid rgba(0,0,0,0.12)'
              : 'none',
            minHeight: '56px',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            backgroundColor: (theme) =>
              alpha(theme.palette.background.paper, 0.6),
          }}
          onClick={() => setScratchpadExpanded(!scratchpadExpanded)}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Scratchpad
          </Typography>
          <IconButton
            size="small"
            sx={{
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.1),
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            {scratchpadExpanded ? (
              <ExpandMoreIcon fontSize="medium" />
            ) : (
              <ExpandLessIcon fontSize="medium" />
            )}
          </IconButton>
        </Box>
        <Collapse
          in={scratchpadExpanded}
          sx={{
            padding: 2,
            paddingTop: 1,
            overflow: 'auto',
            backgroundColor: (theme) =>
              alpha(theme.palette.background.default, 0.3),
          }}
        >
          <Box sx={markdownStyles}>
            <MarkdownRender>
              {scratchpadContent.replaceAll('\n', '\n\n')}
            </MarkdownRender>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
}

export default DocumentViewerContainer;
